import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { toast } from "helpers/toast";
import { errorMessage, fileDownloadResponse } from "helpers/common";
import { api } from "helpers/axios";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Button, Field, NativeSelect } from "components/ui";

export default function Export({ templateKey, tableFields, otherData = null, parentError = null }) {
  const { translate, editableTranslate } = useTranslation();

  const [exportError, setExportError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const { register, reset, getValues, watch, formState: { errors } } = useForm();

  const watchType = watch('type');
  const watchColumns = watch('columns');

  const noColumns = useMemo(() => {
    return watchType === 'pdf' && (watchColumns || []).length < 1;
  }, [watchType, watchColumns]);

  const [totalRows, appliedFilter, search, staffId, quickFilters] = useMemo(() => {
    const {
      totalRows = null,
      appliedFilter = null,
      search = null,
      staffId = null,
      quickFilters = null
    } = otherData || {};

    return [totalRows, appliedFilter, search, staffId, quickFilters];
  }, [otherData]);

  useEffect(() => {
    const columns = tableFields.filter(f => f.displayed).map(f => f.key);

    reset({
      size: 'a4',
      orientation: 'landscape',
      columns
    });
  }, [tableFields]);

  useEffect(() => {
    if (parentError && !exportError) {
      setExportError(parentError);
    }
  }, [parentError]);

  const handleProceed = (e) => {
    e.preventDefault();

    const values = getValues();

    if (values.type === 'pdf') {
      if (values.columns.length < 1) return;

      downloadReport({
        size: values.size,
        orientation: values.orientation,
        columns: values.columns
      }, values.type);
    } else {
      downloadReport({}, values.type);
    }
  };

  const downloadReport = async (formData, type = 'pdf') => {
    setExportError(null);
    setExportLoading(true);

    formData.file_type = type;
    formData.template_key = templateKey;

    formData.send = (totalRows > 1000 ? true : false);

    if (search) {
      formData.search = search;
    }

    if (staffId) {
      formData.staff_id = staffId;
    }

    if (quickFilters && quickFilters.length > 0) {
      formData.quick_filters = quickFilters.join(',');
    }

    if (appliedFilter) {
      formData.saved_filter_hash_id = appliedFilter;
    }

    try {
      const response = await api.post('/exports', formData, {
        responseType: 'blob'
      });
      const { data } = response;

      if (data.type === 'application/json') {
        const json = JSON.parse(await data.text());

        if (json.message === 'Success') {
          toast.success(translate('form.export_email_success'));
        } else {
          throw new Error(json.message);
        }
      } else {
        if (type === 'pdf') {
          fileDownloadResponse(response, 'report.pdf');
        } else if (type === 'csv') {
          fileDownloadResponse(response, 'export.csv');
        } else if (type === 'excel') {
          fileDownloadResponse(response, 'export.xlsx');
        }
      }

      setExportLoading(false);
    } catch (err) {
      let errObj = { message: 'Something went wrong.' };

      try {
        errObj = JSON.parse(await err.response.data.text());
      } catch (err2) {
        if (err2.message) errObj = { message: err2.message };
      }

      setExportLoading(false);
      if (type === 'pdf') {
        setExportError(errorMessage(errObj, 'generate PDF report'));
      } else if (type === 'csv') {
        setExportError(errorMessage(errObj, 'generate CSV export'));
      } else if (type === 'excel') {
        setExportError(errorMessage(errObj, 'generate Excel export'));
      }
      Sentry.captureException(errObj);
    }
  };

  return <form onSubmit={handleProceed}>
    {
      exportError &&
      <div className="mb-2">
        <ErrorAlert error={exportError} onClose={() => setExportError(null)} />
      </div>
    }
    <div className="row">
      <div className="col">
        <Field label={editableTranslate('report.type', 'Format')}>
          <NativeSelect {...register('type')} disabled={parentError} data-testid="type-select">
            <option value="csv">{translate('report.csv_export')}</option>
            <option value="excel">{translate('report.excel_export')}</option>
            <option value="pdf">{translate('report.pdf_report')}</option>
          </NativeSelect>
        </Field>
      </div>
    </div>
    {
      watchType === 'pdf' && <>
        <div className="row mt-3">
          <div className="col">
            <Field label={editableTranslate('report.paper_size')} help={<i data-tooltip-id="paper-size-info" data-tooltip-html={translate('report.paper_size_notes')} className="bi bi-info-circle ml-1"></i>}>
              <ReactTooltip
                id="paper-size-info"
                place="bottom"
                style={{ width: '100%', zIndex: 1000 }} />
              <NativeSelect {...register('size')} data-testid="size-select">
                <option value="a3">A3</option>
                <option value="a4">A4</option>
              </NativeSelect>
            </Field>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col">
            <Field label={editableTranslate('report.paper_orientation')}>
              <NativeSelect {...register('orientation')} data-testid="orientation-select">
                <option value="portrait">{translate('report.portrait')}</option>
                <option value="landscape">{translate('report.landscape')}</option>
              </NativeSelect>
            </Field>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col">
            <label className="peb-field__label">{editableTranslate('report.report_columns')}<span className="peb-field__req">*</span></label>
            <small className="d-block">{editableTranslate('report.report_columns_notes')}</small>

            <div className="mt-2">
              {
                tableFields.map((field, i) => {
                  return <div key={i} className="form-check mb-1">
                    <input type="checkbox" className="form-check-input" id={'modal_column_' + i} value={field.key} data-testid={`export-column-${field.key}`} {...register(`columns`)} />
                    <label className="ml-1 font-weight-normal" htmlFor={'modal_column_' + i}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</label>
                  </div>
                })
              }
            </div>
          </div>
        </div>
      </>
    }
    <div className={`text-center white-sticky-footer pt-2 pb-5 ${watchType !== 'pdf' ? 'mt-3' : ''}`}>
      <Button type="submit" variant="primary" loading={exportLoading} disabled={parentError || noColumns} data-testid="proceed-button">
        {editableTranslate('button.proceed')}
      </Button>
    </div>
  </form>;
}
